/**
 * Pure money rules — no engine dependencies, fully unit-tested. Money is whole pounds (£).
 */
export const MAX_MONEY = 99_999_999;

export type WalletListener = (amount: number, delta: number, reason: string) => void;

/**
 * Who may change the balance. 'local' = single-player (the wallet decides). 'server' = online: the
 * persistent economy lives on the game server, so every local earn/spend/take is refused and the balance
 * only moves through `applyAuthoritative` with figures the server sent. This is THE gate that keeps
 * local game code (missions, shops, debug hooks) from minting money online.
 */
export type MoneyAuthority = 'local' | 'server';

export class Wallet {
  private _balance: number;
  private _authority: MoneyAuthority = 'local';

  constructor(
    initial = 0,
    private readonly onChange: WalletListener = () => undefined,
  ) {
    this._balance = Wallet.clean(initial);
  }

  get balance(): number {
    return this._balance;
  }

  get authority(): MoneyAuthority {
    return this._authority;
  }

  /** Switch authority. Going back to 'local' does not restore a balance; the caller resets it. */
  setAuthority(a: MoneyAuthority): void {
    this._authority = a;
  }

  /** Online: adopt the server's balance (the delta is reported as `reason`). Ignored offline. */
  applyAuthoritative(amount: number, reason: string): boolean {
    if (this._authority !== 'server') return false;
    const next = Wallet.clean(amount);
    const delta = next - this._balance;
    this._balance = next;
    this.onChange(this._balance, delta, reason);
    return true;
  }

  private static clean(n: number): number {
    return Number.isFinite(n) ? Math.max(0, Math.min(MAX_MONEY, Math.round(n))) : 0;
  }

  /**
   * A price or payment as whole pounds >= 0. Not a number (a broken price formula) counts as nothing: before, a NaN
   * went straight into the balance, the save wrote it as null and the next load put the player back to £40.
   */
  private static amount(n: number): number {
    return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
  }

  canAfford(cost: number): boolean {
    return cost <= this._balance;
  }

  /** Add money. Returns the amount actually added. */
  earn(amount: number, reason: string): number {
    if (this._authority !== 'local') return 0;
    const gain = Wallet.amount(amount);
    if (gain === 0) return 0;
    const before = this._balance;
    this._balance = Math.min(MAX_MONEY, before + gain);
    const delta = this._balance - before;
    if (delta !== 0) this.onChange(this._balance, delta, reason);
    return delta;
  }

  /** Pay for something. Fails (returns false, no change) when the player cannot afford it. */
  spend(cost: number, reason: string): boolean {
    if (this._authority !== 'local') return false;
    if (!Number.isFinite(cost)) return false;
    const c = Wallet.amount(cost);
    if (c > this._balance) return false;
    if (c === 0) return true;
    this._balance -= c;
    this.onChange(this._balance, -c, reason);
    return true;
  }

  /** Take up to `amount` (fines, bribes lost on arrest). Never goes below zero. Returns the amount taken. */
  take(amount: number, reason: string): number {
    if (this._authority !== 'local') return 0;
    const t = Math.min(this._balance, Wallet.amount(amount));
    if (t === 0) return 0;
    this._balance -= t;
    this.onChange(this._balance, -t, reason);
    return t;
  }

  /** Set the balance without it counting as income/spend (load, new game). */
  reset(amount: number, reason = 'reset'): void {
    if (this._authority !== 'local') return;
    this._balance = Wallet.clean(amount);
    this.onChange(this._balance, 0, reason);
  }
}

// ----------------------------------------------------------------------------- pricing & penalties

/** Round to the nearest £5 so prices read like prices. */
export const round5 = (n: number): number => Math.max(0, Math.round(n / 5) * 5);

/** Pay-and-spray: base fee plus a premium per heat level (the sprayer asks fewer questions the more you pay). */
export function resprayCost(heat: number): number {
  return 100 + 60 * Math.max(0, Math.min(4, Math.round(heat)));
}

/** Garage patch-up cost for a vehicle at `health`/`maxHealth`. 0 when nothing to fix. */
export function repairCost(health: number, maxHealth: number): number {
  const missing = Math.max(0, maxHealth - health);
  if (missing < 1) return 0;
  return round5(15 + (missing / Math.max(1, maxHealth)) * 120);
}

/** What the coppers take off you when you get nicked: ~12% of your pocket, between £25 and £600. */
export function bustedFine(money: number): number {
  return Math.min(Math.max(0, money), Math.max(25, Math.min(600, round5(money * 0.12))));
}

/** Hospital bill after being wasted: ~6%, between £30 and £400. */
export function hospitalBill(money: number): number {
  return Math.min(Math.max(0, money), Math.max(30, Math.min(400, round5(money * 0.06))));
}

/** Lockup key price (one-off unlock that gives you a free-to-collect getaway car). £2,500 since 2026-10 (was £1,200:
 *  bought straight after the first two jobs, it was the only goal and gone in ten minutes). */
export const LOCKUP_PRICE = 2500;
export const LOCKUP_UNLOCK_ID = 'lockup-car';
/** The car that comes with the lockup (a Vesper GT); Halloran's build sheet dresses it when your motor is one too. */
export const LOCKUP_CAR_SPEC = 'coupe';

/** Format whole pounds with thousands separators: 1234 -> "1,234". */
export function formatMoney(n: number): string {
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Player health model (on-foot damage). Pure so it can be tested: regen starts after a quiet spell. */
export const PLAYER_MAX_HEALTH = 100;
export const REGEN_DELAY = 6;
export const REGEN_RATE = 5;

export class PlayerHealth {
  value = PLAYER_MAX_HEALTH;
  private quiet = 0;

  /** Returns true when this hit killed the player. */
  hurt(amount: number): boolean {
    if (this.value <= 0) return false;
    this.value = Math.max(0, this.value - Math.max(0, amount));
    this.quiet = 0;
    return this.value <= 0;
  }

  update(dt: number): void {
    if (this.value <= 0) return;
    this.quiet += dt;
    if (this.quiet > REGEN_DELAY && this.value < PLAYER_MAX_HEALTH) {
      this.value = Math.min(PLAYER_MAX_HEALTH, this.value + REGEN_RATE * dt);
    }
  }

  reset(): void {
    this.value = PLAYER_MAX_HEALTH;
    this.quiet = 0;
  }
}
